
import { getPhoneDisplayCode } from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import {
  optionClientCategory,
  optionCompanySize
} from "@/app/infrastructures/misc/Constants";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { ClientRegistrationController } from "@/app/ui/controllers/ClientRegistrationController";
import { OptionsClass } from "@/domain/entities/MainApp";
import debounce from "lodash/debounce";
import { prop, Vue, Options } from "vue-class-component";
import ManagingCountryField, { ManagingCountryOption } from "../form-sections/managing-country-field.vue";
import { DistrictData } from "@/domain/entities/Location";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

export type ClientParentSectionProp = {
  companyName: string;
  companyAlias: string;
  clientCategory: OptionsClass;
  companySize: OptionsClass;
  address: string;
  district: string;
  email: string;
  phone: string;
  phoneDialCode?: string;
  isNeedAssessment: boolean;
  managingCountry: ManagingCountryOption | null;
};

export type ClientParentErrorSectionProp = {
  email: boolean;
  phone: boolean;
  alias: boolean;
};

class Props {
  modelValue = prop<ClientParentSectionProp>({
    default: {
      address: "",
      clientCategory: new OptionsClass(),
      companyName: "",
      companyAlias: "",
      companySize: new OptionsClass(),
      district: "",
      email: "",
      phone: "",
      phoneDialCode: "",
      isNeedAssessment: true,
      managingCountry: null
    },
    required: true,
    type: Object
  });
  managingCountryOptions = prop<ManagingCountryOption[]>({
    default: () => [],
    required: false,
    type: Array,
  });
  error = prop<ClientParentErrorSectionProp>({
    default: {
      email: false,
      phone: false,
      alias: false
    }
  });
  isMY = prop<boolean>({
    default: false
  });
}
@Options({
  components: {
    ManagingCountryField
  }
})
class ClientParentSection extends Vue.with(Props) {
  isMYDistrict = false;
  // client category
  clientCategoryList = optionClientCategory;

  //company size
  companySizeList = optionCompanySize;

  get prefix() {
    return this.isMY ? "+60" : "+62";
  }

  get routeTypeName() {
    return this.$route.meta.form;
  }

  get formModel() {
    return this.modelValue;
  }

  set formModel(val) {
    this.$emit("update:modelValue", val);
  }

  get formErrorModel() {
    return this.error;
  }

  set formErrorModel(val) {
    this.$emit("update:error", val);
  }

  get defaultDialCode() {
    const countryCode =
      AccountController.accountData?.country_code ||
      AccountController.accountData?.countryCode;
    return getPhoneDisplayCode(countryCode);
  }

  get errorPhoneParent() {
    return {
      error: this.formErrorModel.phone,
      errorPhone: false,
      errorMandatory: false,
      errorPhoneCode: false,
      phoneCharMinimal: false,
      message: ""
    };
  }

  onSetPhone(v: string) {
    this.$emit("update:modelValue", { ...this.modelValue, phone: v });
    this.validatePhone(v);
  }

  onSetPhoneDialCode(v: string) {
    this.$emit("update:modelValue", { ...this.modelValue, phoneDialCode: v });
  }

  onSetManagingCountry(v: ManagingCountryOption | null) {
    this.formModel.managingCountry = v;
    this.$emit(
      "update:modelValue",
      Object.assign({}, this.modelValue as object, { managingCountry: v })
    );
  }

  mounted(): void {
    this.fetchDistrictList("");
    if (!this.modelValue.phoneDialCode) {
      this.$emit("update:modelValue", {
        ...this.modelValue,
        phoneDialCode: this.defaultDialCode
      });
    }
  }

  // district list
  fetchDistrictList = debounce((search: string) => {
    ClientRegistrationController.getDistrictList({
      search: search
    });
  }, 250);

  get districtList() {
    return ClientRegistrationController.districtList;
  }

  get loadingDistrictList() {
    return ClientRegistrationController.loadingDistrictList;
  }

  validatePhone(value: string) {
    this.formErrorModel.phone = !/^\d+$/.test(value) || value.length < 8;
    if (this.isMYDistrict) this.formErrorModel.phone = !/^\d+$/.test(value);
  }

  formatNumber(value: string) {
    const newValue = value.replace(/^62|^60|^0|[\D]+/g, "");
    return newValue.startsWith("0")
      ? newValue.slice(1, newValue.length)
      : newValue;
  }

  validateEmail(value: string) {
    this.formErrorModel.email = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  validateAlias(value: string) {
    this.formErrorModel.alias = value.length < 3 || value.length > 100
  }

  async onChangeDistrict(item: DistrictData) {
    try {
      MainAppController.showLoading();
      const resp = await ClientRegistrationController.getDistrictDetail(item.id);
      const isMYDistrict = item.countryCode === "MY";

      if (this.isMYDistrict !== isMYDistrict && item.countryCode) {
        this.formModel.phone = "";
      }

      this.isMYDistrict = isMYDistrict;
      this.$emit("change-district", resp);
    } catch (error) {
      // error
      this.$emit("change-district", item);
    } finally {
      MainAppController.closeLoading();
    }
  }
}

export default ClientParentSection;
